import { services } from '@config/services';
import { integrationApi } from './_base';

/**
 * Product-category to tax-code mappings of a tax plugin connection
 * (`/v1/merchantPlugin/{id}/taxCodeMappings*`).
 *
 * Wire shapes, verified against integration-service `TaxCodeMappingController`
 * on origin/main:
 *
 *   GET    /integration/v1/merchantPlugin/{id}/taxCodeMappings
 *   PUT    /integration/v1/merchantPlugin/{id}/taxCodeMappings
 *          body: { "mappings": [{ "category": "...", "taxCode": "..." }] }
 *   PUT    /integration/v1/merchantPlugin/{id}/taxCodeMappings/{category}
 *          body: { "taxCode": "..." }
 *   DELETE /integration/v1/merchantPlugin/{id}/taxCodeMappings/{category}
 *
 * Every one of them answers with the whole collection:
 *   { "merchantPluginId": "...", "mappings": [{ "category": "...", "taxCode": "..." }] }
 *
 * The service sorts by category and compares categories case-insensitively, so
 * the order on screen is the server's and the client never re-sorts.
 */

export interface TaxCodeMapping {
  category: string;
  /** Null or empty means unmapped: the provider account default applies. */
  taxCode?: string | null;
}

export interface TaxCodeMappings {
  merchantPluginId: string;
  mappings: TaxCodeMapping[];
}

/**
 * Upper bound of one bulk replace, matching
 * `TaxCodeMappingRules.MaxMappings`. Checked before upload so an oversized
 * file is refused with a readable message instead of a 400.
 */
export const MAX_TAX_CODE_MAPPINGS = 1000;

/**
 * Whether a category can travel as the `{category}` path segment of the
 * single-row routes.
 *
 * A URL resolves "." and ".." as dot segments, and `encodeURIComponent` leaves
 * dots alone: "." lands on the bulk replace, whose body then carries no
 * mappings, and ".." lands on the connection itself, whose DELETE removes it.
 * The service keeps an encoded "/" undecoded, so a slash reaches it as the
 * literal text "%2F" and addresses a different row.
 */
export function isPathSafeCategory(category: string): boolean {
  const trimmed = category.trim();
  return trimmed !== '.' && trimmed !== '..' && !trimmed.includes('/');
}

/**
 * A category travels in the path, so it is encoded, and one the path cannot
 * carry is refused before any request is built. The UI refuses those names
 * first; this guard also covers a row stored before it did.
 */
function mappingUrl(merchantPluginId: string, category?: string): string {
  const base = `${services.integration}/v1/merchantPlugin/${encodeURIComponent(merchantPluginId)}/taxCodeMappings`;
  if (category === undefined) return base;
  if (!isPathSafeCategory(category)) {
    throw new Error(`The category "${category}" cannot be changed one row at a time.`);
  }
  return `${base}/${encodeURIComponent(category)}`;
}

/**
 * Request builders, exported so the wire shape is asserted directly rather than
 * inferred from a mounted component (the house pattern — see
 * `versionManagementRequestBuilders`).
 */
export const taxCodeMappingRequestBuilders = {
  get: (merchantPluginId: string) => ({
    url: mappingUrl(merchantPluginId),
    method: 'GET',
  }),
  replace: ({
    merchantPluginId,
    mappings,
  }: {
    merchantPluginId: string;
    mappings: TaxCodeMapping[];
  }) => ({
    url: mappingUrl(merchantPluginId),
    method: 'PUT',
    body: { mappings },
  }),
  upsert: ({
    merchantPluginId,
    category,
    taxCode,
  }: {
    merchantPluginId: string;
    category: string;
    taxCode?: string | null;
  }) => ({
    url: mappingUrl(merchantPluginId, category),
    method: 'PUT',
    body: { taxCode },
  }),
  remove: ({
    merchantPluginId,
    category,
  }: {
    merchantPluginId: string;
    category: string;
  }) => ({
    url: mappingUrl(merchantPluginId, category),
    method: 'DELETE',
  }),
};

/**
 * Normalizes the envelope. Every endpoint returns the same body, and a
 * connection that has never been mapped answers with `mappings` absent rather
 * than an empty array, which would otherwise reach the table as undefined.
 */
export function transformMappings(
  response: Partial<TaxCodeMappings> | null,
  merchantPluginId: string,
): TaxCodeMappings {
  return {
    merchantPluginId: response?.merchantPluginId ?? merchantPluginId,
    mappings: Array.isArray(response?.mappings) ? response.mappings : [],
  };
}

const taxCodeMappingsApi = integrationApi.injectEndpoints({
  endpoints: (builder) => ({
    getTaxCodeMappings: builder.query<TaxCodeMappings, string>({
      query: (merchantPluginId) => taxCodeMappingRequestBuilders.get(merchantPluginId),
      transformResponse: (response: Partial<TaxCodeMappings> | null, _meta, merchantPluginId) =>
        transformMappings(response, merchantPluginId),
      providesTags: (_r, _e, merchantPluginId) => [
        { type: 'TaxCodeMappings', id: merchantPluginId },
      ],
    }),
    /** Bulk replace. The CSV import parses client-side and calls this. */
    replaceTaxCodeMappings: builder.mutation<
      TaxCodeMappings,
      { merchantPluginId: string; mappings: TaxCodeMapping[] }
    >({
      query: (args) => taxCodeMappingRequestBuilders.replace(args),
      transformResponse: (response: Partial<TaxCodeMappings> | null, _meta, { merchantPluginId }) =>
        transformMappings(response, merchantPluginId),
      invalidatesTags: (_r, _e, { merchantPluginId }) => [
        { type: 'TaxCodeMappings', id: merchantPluginId },
      ],
    }),
    /** Adds or updates one category. An inline row save calls this. */
    upsertTaxCodeMapping: builder.mutation<
      TaxCodeMappings,
      { merchantPluginId: string; category: string; taxCode?: string | null }
    >({
      query: (args) => taxCodeMappingRequestBuilders.upsert(args),
      transformResponse: (response: Partial<TaxCodeMappings> | null, _meta, { merchantPluginId }) =>
        transformMappings(response, merchantPluginId),
      invalidatesTags: (_r, _e, { merchantPluginId }) => [
        { type: 'TaxCodeMappings', id: merchantPluginId },
      ],
    }),
    deleteTaxCodeMapping: builder.mutation<
      TaxCodeMappings,
      { merchantPluginId: string; category: string }
    >({
      query: (args) => taxCodeMappingRequestBuilders.remove(args),
      transformResponse: (response: Partial<TaxCodeMappings> | null, _meta, { merchantPluginId }) =>
        transformMappings(response, merchantPluginId),
      invalidatesTags: (_r, _e, { merchantPluginId }) => [
        { type: 'TaxCodeMappings', id: merchantPluginId },
      ],
    }),
  }),
});

export const {
  useGetTaxCodeMappingsQuery,
  useReplaceTaxCodeMappingsMutation,
  useUpsertTaxCodeMappingMutation,
  useDeleteTaxCodeMappingMutation,
} = taxCodeMappingsApi;
